import FormAlert from "./FormAlert";
import { useState } from "react";
import { useSetAdminUserUsernameMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { Field, FieldLabel } from "../field";
import { Input } from "../input";

export default function AdminUsernameDialog({
  open,
  onOpenChange,
  userID,
  currentUsername,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userID: string;
  currentUsername: string;
}) {
  const [username, setUsername] = useState(currentUsername);
  const [localError, setLocalError] = useState<string | null>(null);
  const [setUsernameMutation, { isLoading }] =
    useSetAdminUserUsernameMutation();

  const handleOpenChange = (nextOpen: boolean) => {
    // Start from the stored handle each time, not from an abandoned edit.
    setUsername(currentUsername);
    setLocalError(null);
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    // The server lowercases and validates; this only spares a round trip
    // for a change that is not one.
    if (username.trim().toLowerCase() === currentUsername) {
      handleOpenChange(false);
      return;
    }
    try {
      await setUsernameMutation({ userID, username }).unwrap();
      handleOpenChange(false);
    } catch (e) {
      setLocalError(
        getApiErrorDetail(
          e,
          "Could not change that username. Please try again.",
        ),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {/* The form must live inside DialogContent, not wrap it: DialogContent
            portals its children to document.body, so a <form> wrapping it
            would have no real DOM descendants and its submit button would
            never fire an onSubmit. */}
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Change username</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              The user signs in with the new username from now on. Their
              password and current sessions are unchanged.
            </p>
            <Field>
              <FieldLabel htmlFor="adminChangeUsername">Username</FieldLabel>
              <Input
                id="adminChangeUsername"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
              />
            </Field>
            <FormAlert error={localError} />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isLoading || !username.trim()}>
              Change username
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
